import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";

import Container from "../../../../components/Container";
import Loader from "../../../../components/Loader";
import AlertMsg from "../../../../components/AlertMsg";
import RestaurantHeader from "../../../../components/RestaurantHeader";
import UploadFile from "../../../../components/UploadFile";

import { CURRENCIES, CURRENCY_SYMBOL_MAP } from "../../../../constants";
import * as Api from "../../../../api";

import { createDiscountSchema } from "../schemas";
import { TCreateDiscountRequest } from "../types";
import {
  useGetDiscount,
  useGetCategories,
  useCreateDiscount,
  useUpdateDiscount,
  TCategory,
} from "../services";

const ORDER_TYPES = [
  { value: 1, label: "Dine-In" },
  { value: 2, label: "Delivery" },
  { value: 3, label: "Pick Up" },
  { value: 4, label: "Table" },
];

const DiscountFormPage = () => {
  const { rest_id: restaurantId, id } = useParams<{ rest_id: string; id: string }>();
  const navigate = useNavigate();

  const isEditMode = !!id;

  const [isTiered, setIsTiered] = useState(false);
  const [currency, setCurrency] = useState("GBP");
  const [selectedCategories, setSelectedCategories] = useState<TCategory[]>([]);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [imageList, setImageList] = useState<any[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  const { data: discount, isLoading: isFetching } = useGetDiscount(
    id || "",
    restaurantId || "",
  );
  const { data: categoryList = [] } = useGetCategories(restaurantId || "");

  const createMutation = useCreateDiscount(restaurantId || "");
  const updateMutation = useUpdateDiscount(restaurantId || "");

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const currencySymbol =
    (CURRENCY_SYMBOL_MAP as Record<string, string>)[currency] || currency;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TCreateDiscountRequest>({
    resolver: zodResolver(createDiscountSchema),
    defaultValues: {
      type: "discount",
      description: "",
      discountType: "percentage",
      discountPercentage: undefined,
      discountAmount: undefined,
      maxDiscount: undefined,
      minPurchase: undefined,
      tiers: [],
      applicableOrderType: [],
      validFrom: "",
      validTo: "",
      usageLimit: undefined,
      userUsageLimit: undefined,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "tiers" });
  const discountType = watch("discountType");

  const handleUploadFile = (data: any, file: File) => {
    setImageLoading(true);
    setImageList([data]);
    const formData = new FormData();
    formData.append("image", file);
    Api.uploadImageFile(formData).then((response: any) => {
      if (response.success) {
        const url: string = response.data.location;
        data.name = response.data.fileId;
        data.image = url;
        data.upload = true;
        setImageList([{ ...data }]);
        setBannerImage(url);
      } else {
        setImageList([]);
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
      setImageLoading(false);
    });
  };

  const handleRemoveImage = () => {
    setImageList([]);
    setBannerImage(null);
  };

  const handleUploadError = (err: string) => {
    setMsgAlert({ open: true, message: err, msgType: "error" });
  };
  const isCategoriesDisabled = discountType === "fixed" && !isTiered;

  // Populate form when editing
  useEffect(() => {
    if (!discount || !isEditMode) return;

    const hasTiers = discount.tiers && discount.tiers.length > 0;
    setIsTiered(!!hasTiers);

    const discountCurrency =
      discount.discountAmount?.currency ||
      discount.maxDiscount?.currency ||
      discount.minPurchase?.currency ||
      "GBP";
    setCurrency(discountCurrency);

    if (discount._idCategories?.length && categoryList.length > 0) {
      setSelectedCategories(
        categoryList.filter((cat) => discount._idCategories!.includes(cat._id)),
      );
    } else {
      setSelectedCategories([]);
    }

    if (discount.bannerImage) {
      setBannerImage(discount.bannerImage);
      setImageList([{
        id: new Date(),
        name: discount.bannerImage,
        displayName: "Banner Image",
        size: "0",
        image: discount.bannerImage,
        upload: true,
      }]);
    } else {
      setBannerImage(null);
      setImageList([]);
    }

    reset({
      description: discount.description,
      discountType: discount.discountType,
      discountPercentage: discount.discountPercentage,
      discountAmount: discount.discountAmount,
      maxDiscount: discount.maxDiscount,
      minPurchase: discount.minPurchase,
      tiers: discount.tiers || [],
      applicableOrderType: discount.applicableOrderType,
      validFrom: discount.validFrom ? discount.validFrom.slice(0, 16) : "",
      validTo: discount.validTo ? discount.validTo.slice(0, 16) : "",
      usageLimit: discount.usageLimit,
      userUsageLimit: discount.userUsageLimit,
    });
  }, [discount, isEditMode, reset, categoryList]);

  useEffect(() => {
    if (isCategoriesDisabled) setSelectedCategories([]);
  }, [isCategoriesDisabled]);

  const onFormSubmit = async (data: TCreateDiscountRequest) => {
    const payload: TCreateDiscountRequest = {
      type: "discount",
      description: data.description.trim(),
      discountType: data.discountType,
      applicableOrderType: data.applicableOrderType,
      validFrom: new Date(data.validFrom).toISOString(),
      validTo: new Date(data.validTo).toISOString(),
    };

    if (isTiered && data.tiers && data.tiers.length > 0) {
      payload.tiers = data.tiers.map((tier) => {
        const cleanTier: typeof tier = { minPurchase: tier.minPurchase };
        if (data.discountType === "percentage") {
          cleanTier.discountPercentage = tier.discountPercentage;
          if (tier.maxDiscount?.value) cleanTier.maxDiscount = tier.maxDiscount;
        } else {
          cleanTier.discountAmount = tier.discountAmount;
        }
        return cleanTier;
      });
    } else {
      if (data.discountType === "percentage") {
        payload.discountPercentage = data.discountPercentage;
        if (data.maxDiscount?.value) payload.maxDiscount = data.maxDiscount;
      } else {
        payload.discountAmount = data.discountAmount;
      }
      if (data.minPurchase?.value) payload.minPurchase = data.minPurchase;
    }

    if (data.usageLimit) payload.usageLimit = data.usageLimit;
    if (data.userUsageLimit) payload.userUsageLimit = data.userUsageLimit;

    if (!isCategoriesDisabled && selectedCategories.length > 0) {
      payload._idCategories = selectedCategories.map((cat) => cat._id);
    } else {
      payload._idCategories = [];
    }

    payload.bannerImage = bannerImage ?? null;

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: id!, payload });
        setMsgAlert({
          open: true,
          message: "Discount updated successfully",
          msgType: "success",
        });
      } else {
        await createMutation.mutateAsync(payload);
        setMsgAlert({
          open: true,
          message: "Discount created successfully",
          msgType: "success",
        });
      }
      navigate(`/restaurant/${restaurantId}/discounts`);
    } catch {
      setMsgAlert({
        open: true,
        message: isEditMode
          ? "Failed to update discount"
          : "Failed to create discount",
        msgType: "error",
      });
    }
  };

  const handleAddTier = () => {
    append({
      minPurchase: { value: 0, currency },
      ...(discountType === "percentage"
        ? { discountPercentage: 0 }
        : { discountAmount: { value: 0, currency } }),
    });
  };

  if (isEditMode && isFetching) {
    return (
      <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
        <Box sx={styles.loaderContainer}>
          <Loader />
        </Box>
      </Container>
    );
  }

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <RestaurantHeader
            title={isEditMode ? "Edit Discount" : "Add Discount"}
            onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/discounts`)}
          />
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={2} pb={3}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={3}>
              {/* Left Column: Discount Details */}
              <Grid item xs={12} md={6}>
                <Box sx={styles.section}>
                  <Typography sx={styles.sectionLabel}>
                    Discount Details
                  </Typography>

                  <Box sx={styles.sectionContent}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: isTiered ? "#999" : "#000",
                          fontSize: "14px",
                          fontWeight: isTiered ? 400 : 600,
                        }}
                      >
                        Simple Discount
                      </Typography>
                      <Switch
                        checked={isTiered}
                        onChange={(e) => setIsTiered(e.target.checked)}
                      />
                      <Typography
                        sx={{
                          color: isTiered ? "#000" : "#999",
                          fontSize: "14px",
                          fontWeight: isTiered ? 600 : 400,
                        }}
                      >
                        Tiered Discount
                      </Typography>
                    </Box>

                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          fullWidth
                          required
                          multiline
                          rows={2}
                        />
                      )}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Controller
                          name="discountType"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Discount Type"
                              select
                              fullWidth
                            >
                              <MenuItem value="percentage">Percentage</MenuItem>
                              <MenuItem value="fixed">Fixed Amount</MenuItem>
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          label="Currency"
                          select
                          fullWidth
                        >
                          {CURRENCIES.map(
                            (c: {
                              code: string;
                              symbol: string;
                              name: string;
                            }) => (
                              <MenuItem key={c.code} value={c.code}>
                                {c.code} - {c.symbol} ({c.name})
                              </MenuItem>
                            ),
                          )}
                        </TextField>
                      </Grid>
                    </Grid>

                    {!isTiered ? (
                      <>
                        {discountType === "percentage" ? (
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Controller
                                name="discountPercentage"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined,
                                      )
                                    }
                                    label="Discount %"
                                    type="number"
                                    fullWidth
                                    error={!!errors.discountPercentage}
                                    helperText={errors.discountPercentage?.message}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name="maxDiscount"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    value={field.value?.value ?? ""}
                                    onChange={(e) =>
                                      field.onChange(
                                        e.target.value
                                          ? {
                                            value: Number(e.target.value),
                                            currency,
                                          }
                                          : undefined,
                                      )
                                    }
                                    label="Max Discount"
                                    type="number"
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          {currencySymbol}
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        ) : (
                          <Controller
                            name="discountAmount"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                value={field.value?.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value
                                      ? {
                                        value: Number(e.target.value),
                                        currency,
                                      }
                                      : undefined,
                                  )
                                }
                                label="Discount Amount"
                                type="number"
                                fullWidth
                                error={!!errors.discountAmount}
                                helperText={errors.discountAmount?.message}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      {currencySymbol}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}

                        <Controller
                          name="minPurchase"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              value={field.value?.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? {
                                      value: Number(e.target.value),
                                      currency,
                                    }
                                    : undefined,
                                )
                              }
                              label="Min Purchase Amount"
                              type="number"
                              fullWidth
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {currencySymbol}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </>
                    ) : (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ color: "#666", fontSize: "14px" }}>
                            Tiers
                          </Typography>
                          <Button
                            onClick={handleAddTier}
                            sx={styles.addTierBtn}
                          >
                            + Add Tier
                          </Button>
                        </Box>
                        {fields.map((field, index) => (
                          <Box
                            key={field.id}
                            sx={{
                              mb: 1,
                              p: 1.5,
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                sx={{ color: "#666", fontSize: "12px" }}
                              >
                                Tier {index + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                                sx={styles.removeTierBtn}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={styles.tierRow}>
                              <Controller
                                name={`tiers.${index}.minPurchase`}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    value={f.value?.value ?? ""}
                                    onChange={(e) =>
                                      f.onChange(
                                        e.target.value
                                          ? {
                                            value: Number(e.target.value),
                                            currency,
                                          }
                                          : { value: 0, currency },
                                      )
                                    }
                                    label="Min Purchase"
                                    type="number"
                                    size="small"
                                    sx={{ flex: 1 }}
                                    error={!!errors.tiers?.[index]?.minPurchase}
                                    helperText={errors.tiers?.[index]?.minPurchase?.message}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          {currencySymbol}
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                              {discountType === "percentage" ? (
                                <>
                                  <Controller
                                    name={`tiers.${index}.discountPercentage`}
                                    control={control}
                                    render={({ field: f }) => (
                                      <TextField
                                        {...f}
                                        onChange={(e) =>
                                          f.onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : 0,
                                          )
                                        }
                                        label="Discount %"
                                        type="number"
                                        size="small"
                                        sx={{ flex: 1 }}
                                        error={!!errors.tiers?.[index]?.discountPercentage}
                                        helperText={errors.tiers?.[index]?.discountPercentage?.message}
                                      />
                                    )}
                                  />
                                  <Controller
                                    name={`tiers.${index}.maxDiscount`}
                                    control={control}
                                    render={({ field: f }) => (
                                      <TextField
                                        value={f.value?.value ?? ""}
                                        onChange={(e) =>
                                          f.onChange(
                                            e.target.value
                                              ? {
                                                value: Number(e.target.value),
                                                currency,
                                              }
                                              : undefined,
                                          )
                                        }
                                        label="Max Discount"
                                        type="number"
                                        size="small"
                                        sx={{ flex: 1 }}
                                        error={!!errors.tiers?.[index]?.maxDiscount}
                                        helperText={errors.tiers?.[index]?.maxDiscount?.message}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              {currencySymbol}
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    )}
                                  />
                                </>
                              ) : (
                                <Controller
                                  name={`tiers.${index}.discountAmount`}
                                  control={control}
                                  render={({ field: f }) => (
                                    <TextField
                                      value={f.value?.value ?? ""}
                                      onChange={(e) =>
                                        f.onChange(
                                          e.target.value
                                            ? {
                                              value: Number(e.target.value),
                                              currency,
                                            }
                                            : { value: 0, currency },
                                        )
                                      }
                                      label="Discount Amount"
                                      type="number"
                                      size="small"
                                      sx={{ flex: 1 }}
                                      error={!!errors.tiers?.[index]?.discountAmount}
                                      helperText={errors.tiers?.[index]?.discountAmount?.message}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            {currencySymbol}
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}
                        {fields.length === 0 && (errors.discountPercentage || errors.discountAmount) && (
                          <Typography sx={{ color: "#f44336", fontSize: "12px" }}>
                            Please add at least one tier
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box>
                      <Typography sx={styles.sectionLabel}>Banner Image</Typography>
                      <UploadFile
                        page="addResItemImg"
                        imageList={imageList}
                        imageLoading={imageLoading}
                        uploadFile={handleUploadFile}
                        uploadError={handleUploadError}
                        onRemoveImage={handleRemoveImage}
                        maxFiles={1}
                        uploadText="Drag & drop a banner image here"
                        uploadHeight="180px"
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Right Column: Rules & Limits */}
              <Grid item xs={12} md={6}>
                <Box sx={styles.section}>
                  <Typography sx={styles.sectionLabel}>
                    Rules & Limits
                  </Typography>

                  <Box sx={styles.sectionContent}>
                    <Controller
                      name="applicableOrderType"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          options={ORDER_TYPES}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.label}
                          value={ORDER_TYPES.filter((ot) =>
                            field.value?.includes(ot.value),
                          )}
                          onChange={(_, newValue) =>
                            field.onChange(newValue.map((v) => v.value))
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Applicable Order Types"
                            />
                          )}
                        />
                      )}
                    />

                    <Autocomplete
                      multiple
                      options={categoryList}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.name}
                      value={selectedCategories}
                      onChange={(_, newValue) =>
                        setSelectedCategories(newValue)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Applicable Categories"
                          helperText={
                            isCategoriesDisabled
                              ? "Not available for simple fixed discounts"
                              : ""
                          }
                        />
                      )}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Controller
                          name="validFrom"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Valid From"
                              type="datetime-local"
                              fullWidth
                              error={!!errors.validFrom}
                              helperText={errors.validFrom?.message}
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name="validTo"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Valid To"
                              type="datetime-local"
                              fullWidth
                              error={!!errors.validTo}
                              helperText={errors.validTo?.message}
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Controller
                          name="usageLimit"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                )
                              }
                              value={field.value ?? ""}
                              label="Total Usage Limit"
                              type="number"
                              fullWidth
                              helperText="Leave empty for unlimited"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name="userUsageLimit"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                )
                              }
                              value={field.value ?? ""}
                              label="Per-User Usage Limit"
                              type="number"
                              fullWidth
                              helperText="Leave empty for unlimited"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Full-width Action Buttons */}
              <Grid item xs={12}>
                <Box sx={styles.actions}>
                  <Button
                    sx={styles.cancelBtn}
                    onClick={() =>
                      navigate(`/restaurant/${restaurantId}/discounts`)
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    sx={styles.submitBtn}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : isEditMode
                        ? "Update Discount"
                        : "Create Discount"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default DiscountFormPage;

const styles = {
  mainView: {
    backgroundColor: "#FFF",
    borderRadius: "8px",
    minHeight: "calc(100vh - 200px)",
  },
  loaderContainer: {
    width: "100%",
    minHeight: "calc(100vh - 200px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  section: {
    border: "1px solid #ddd",
    borderRadius: "12px",
    padding: "20px",
    height: "100%",
  },
  sectionLabel: {
    color: "#666",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "16px",
    fontFamily: "GilroyBold",
  },
  sectionContent: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "20px",
  },
  tierRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap" as const,
  },
  addTierBtn: {
    backgroundColor: "#000",
    color: "#fff",
    fontSize: "12px",
    textTransform: "capitalize" as const,
    borderRadius: "6px",
    px: 2,
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  removeTierBtn: {
    color: "#f44336",
    "&:hover": {
      backgroundColor: "rgba(244, 67, 54, 0.1)",
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
  },
  cancelBtn: {
    color: "#000",
    border: "2px solid #000",
    borderRadius: "8px",
    textTransform: "capitalize" as const,
    px: 3,
    fontFamily: "GilroySemiBold",
  },
  submitBtn: {
    backgroundColor: "#000",
    color: "#fff",
    borderRadius: "8px",
    textTransform: "capitalize" as const,
    px: 3,
    fontFamily: "GilroySemiBold",
    "&:hover": {
      backgroundColor: "#333",
    },
    "&:disabled": {
      opacity: 0.5,
      color: "#fff",
    },
  },
};
